import { invoiceCode } from '@/function/commercial/invoice.state';
import { quoteCode } from '@/function/commercial/quote.code';
import { STATE_LABEL } from '@/function/work/board.signals';
import type { TypeInvoice } from '@/types/commercial/types.commercial.invoice';
import type { TypeQuote } from '@/types/commercial/types.commercial.quote';
import type { TypeCustomer } from '@/types/customer/types.customer';
import type { TypeNavEntity } from '@/types/ui/types.ui.navigation';
import type { TypeProject } from '@/types/work/types.work.project';
import type { TypeTask } from '@/types/work/types.work.task';

type TypeSource = {
    customers: TypeCustomer[];
    projects: TypeProject[];
    tasks: TypeTask[];
    quotes: TypeQuote[];
    invoices: TypeInvoice[];
};

const CUSTOMER_WORD: Record<TypeCustomer['status'], string> = {
    prospect: 'Potenziale',
    active: 'Cliente',
    archived: 'Cliente archiviato',
};

const PROJECT_WORD: Record<TypeProject['status'], string> = {
    active: 'In corso',
    paused: 'In pausa',
    done: 'Consegnato',
};

const words = (...parts: (string | null | undefined)[]): string[] => parts
    .filter((part): part is string => Boolean(part))
    .flatMap((part) => part.toLowerCase().split(/[\s/·.,-]+/))
    .filter((word) => word.length > 2);

export default function navigationEntities(source: TypeSource): TypeNavEntity[] {
    const projectById = new Map(source.projects.map((project) => [project.id, project]));
    const customerById = new Map(source.customers.map((customer) => [customer.id, customer]));

    const customers: TypeNavEntity[] = source.customers.map((customer) => ({
        id: customer.id,
        kind: 'customer',
        label: customer.name,
        hint: [CUSTOMER_WORD[customer.status], customer.address.city].filter(Boolean).join(' · '),
        path: `/clients/${customer.id}`,
        parentId: 'clients',
        keywords: words(customer.name, customer.vatNumber, customer.fiscalCode, ...customer.tags),
    }));

    const projects: TypeNavEntity[] = source.projects.map((project) => ({
        id: project.id,
        kind: 'project',
        label: project.name,
        hint: [project.code, PROJECT_WORD[project.status], customerById.get(project.customerId ?? '')?.name]
            .filter(Boolean).join(' · '),
        path: `/work/project/${project.id}`,
        parentId: 'work.board',
        keywords: words(project.name, project.code, ...project.tags),
    }));

    const tasks: TypeNavEntity[] = source.tasks.map((task) => ({
        id: task.id,
        kind: 'task',
        label: task.title,
        hint: [projectById.get(task.projectId)?.code, STATE_LABEL[task.state]].filter(Boolean).join(' · '),
        path: `/work/task/${task.id}`,
        parentId: 'work.board',
        keywords: words(task.title),
    }));

    const quotes: TypeNavEntity[] = source.quotes.map((quote) => ({
        id: quote.id,
        kind: 'quote',
        label: `Preventivo ${quoteCode(quote)}`,
        hint: [quote.subject, customerById.get(quote.customerId ?? '')?.name].filter(Boolean).join(' · '),
        path: `/commercial/quotes/${quote.id}`,
        parentId: 'commercial.quotes',
        keywords: words(quote.subject, customerById.get(quote.customerId ?? '')?.name),
    }));

    const invoices: TypeNavEntity[] = source.invoices.map((invoice) => ({
        id: invoice.id,
        kind: 'invoice',
        label: `Fattura ${invoiceCode(invoice)}`,
        hint: [invoice.subject, customerById.get(invoice.customerId)?.name].filter(Boolean).join(' · '),
        path: `/commercial/invoices/${invoice.id}`,
        parentId: 'commercial.invoices',
        keywords: words(invoice.subject, customerById.get(invoice.customerId)?.name),
    }));

    return [...customers, ...projects, ...tasks, ...quotes, ...invoices];
}
